import CaptureFooter from "@/components/lp/CaptureFooter";
import { Suspense, lazy, useCallback, useEffect, useState } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useLiteEnabled } from "@/hooks/useLiteEnabled";
import { useAuthState } from "@/hooks/useAuthState";
import { useLocalizedRoute } from "@/hooks/useLocalizedRoute";
import SEO from "@/components/SEO";
import FullLeakDetectorCard from "@/components/tools/FullLeakDetectorCard";
import { Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useHasActivePanel } from "@/hooks/useHasActivePanel";
import { usePlanGate } from "@/hooks/usePlanUi";
import { startProCheckout } from "@/lib/proCheckout";
import { setPostAuthIntent } from "@/lib/post-auth-intent";
import {
  ANALISIS_COPY,
  ANALISIS_PATH_ES,
  ANALISIS_PATH_EN,
} from "@/lib/analisisCompletoSeo";

const LazyAuthModal = lazy(() => import("@/components/LazyAuthModal"));
const LazyExpress = lazy(() => import("@/components/express/ExpressLeakDetector"));

/**
 * /herramientas/analisis-completo — Detector de Fugas Completo.
 * Vive dentro de la cáscara del panel (menú lateral + barra superior + footer
 * estándar, que aporta `PublicShell`); aquí solo va el contenido del main:
 * hero sobrio, card con el flujo embebido y un bloque de ratios + FAQ.
 */
const AnalisisCompleto = () => {
  useLocalizedRoute();
  const { lang, t } = useLanguage();
  const liteEnabled = useLiteEnabled();
  const L: "es" | "en" = lang === "en" ? "en" : "es";
  const es = L === "es";
  const { isAuthenticated, isReady } = useAuthState();
  const { hasPanel, isAdmin, loading: panelLoading } = useHasActivePanel();
  // Lite y Básico: solo exprés. Completo desde Club Pro (tabla de precios).
  const canUseFull = usePlanGate("tool.detector_completo", hasPanel || isAdmin).allowed && isAuthenticated;
  const [expressOpen, setExpressOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [intentPath, setIntentPath] = useState("/dashboard");
  const [isResult, setIsResult] = useState(false);

  const openAuth = useCallback((path: string) => {
    setIntentPath(path);
    setPostAuthIntent({ kind: "path", path });
    setAuthOpen(true);
  }, []);

  const onPhaseChange = useCallback((phase: string) => setIsResult(phase === "result" || phase === "detail"), []);

  useEffect(() => {
    if (isReady && isAuthenticated && authOpen) setAuthOpen(false);
  }, [isReady, isAuthenticated, authOpen]);

  const path = es ? ANALISIS_PATH_ES : ANALISIS_PATH_EN;

  return (
    <main className="flex flex-1 flex-col overflow-x-hidden bg-background">
      <SEO
        title={ANALISIS_COPY.title[L]}
        description={ANALISIS_COPY.description[L]}
        path={path}
        pathEs={ANALISIS_PATH_ES}
        pathEn={ANALISIS_PATH_EN}
        image="https://retaillemon.com/assets/og/og-termometro.jpg"
        imageAlt={ANALISIS_COPY.ogTitle[L]}
        lang={L}
        robots={isResult ? "noindex, follow" : "index, follow"}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Retail Lemon", item: "https://retaillemon.com/" },
              { "@type": "ListItem", position: 2, name: ANALISIS_COPY.h1[L], item: `https://retaillemon.com${path}` },
            ],
          },
        ]}
      />

      {authOpen && (
        <Suspense fallback={null}>
          <LazyAuthModal
            open={authOpen}
            onOpenChange={setAuthOpen}
            initialMode="signup"
            intent={{ kind: "path", path: intentPath }}
          />
        </Suspense>
      )}

      {/* Cabecera con el mismo molde que las demás herramientas (TabInfoHeader) */}
      <div className="mt-6 border-b border-gray-100">
        <div className="mx-auto max-w-3xl px-4 pb-4 pt-1 text-center">
          <div className="mb-2 flex h-8 items-center justify-center">
            <span className="inline-flex h-8 items-center gap-2 rounded-full bg-foreground px-4 font-body text-sm font-semibold text-background">
              {liteEnabled ? (es ? "Exprés en Lite · Completo desde Club Pro" : "Express in Lite · Full from Club Pro") : (es ? "Diagnóstico exprés · Completo desde Club Pro" : "Express diagnosis · Full from Club Pro")}
            </span>
          </div>
          <h1 className="mb-1.5 font-serif text-2xl text-neutral-900 sm:text-3xl">
            {ANALISIS_COPY.h1[L]}
          </h1>
          <p className="text-sm leading-relaxed text-neutral-500">
            {ANALISIS_COPY.sub[L]}
          </p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-2xl px-4 pb-8 pt-4">
        {!isReady || (isAuthenticated && panelLoading) ? (
          <div className="mt-4 h-[340px] animate-pulse rounded-2xl bg-muted" aria-busy="true" />
        ) : canUseFull ? (
          <FullLeakDetectorCard id="detector-completo" onOpenAuth={openAuth} onPhaseChange={onPhaseChange} />
        ) : (
          <div className="mt-4 flex flex-col items-center gap-3 rounded-2xl border border-border bg-card p-6 text-center">
            <Lock className="h-6 w-6 text-muted-foreground" strokeWidth={1.5} aria-hidden="true" />
            <h2 className="font-display text-lg font-bold">
              {es ? "El análisis completo es de Club Pro" : "The full analysis is part of Club Pro"}
            </h2>
            <p className="max-w-md text-sm text-muted-foreground">
              {es
                ? `${isAuthenticated ? "Con tu plan" : "Gratis"} puedes hacer la versión exprés ahora mismo. El análisis completo por áreas se abre desde Club Pro.`
                : `${isAuthenticated ? "With your plan" : "For free"} you can run the express version right now. The full area-by-area analysis opens with Club Pro.`}
            </p>
            <div className="flex w-full max-w-sm flex-col gap-2">
              {isAuthenticated ? (
                <Button
                  className="h-12 w-full font-semibold"
                  onClick={() => void startProCheckout({ isAuthenticated: true, lang: L })}
                >
                  {es ? "Pasar a Club Pro" : "Upgrade to Club Pro"}
                </Button>
              ) : (
                <Button className="h-12 w-full font-semibold" onClick={() => openAuth(path)}>
                  {es ? "Unirme al acceso anticipado" : "Join early access"}
                </Button>
              )}
              <Button variant="secondary" className="h-12 w-full font-semibold" onClick={() => setExpressOpen(true)}>
                {es ? "Hacer el Detector exprés" : "Run the express detector"}
              </Button>
            </div>
          </div>
        )}
      </div>
      {expressOpen && (
        <Suspense fallback={null}>
          <LazyExpress open={expressOpen} onOpenChange={setExpressOpen} />
        </Suspense>
      )}
      <CaptureFooter slug="detector_fugas" pathEs="/detector-de-fugas" pathEn="/en/leak-detector" shareText={t("shareLanding", "leakText")} />
    </main>
  );
};

export default AnalisisCompleto;
